/** Centralized API Configuration & Base URL Resolver */

const DEFAULT_API_BASE = "https://api.iktravels.com";

/** Resolve clean API_BASE_URL without trailing slashes */
export const API_BASE_URL = (() => {
  const envUrl = process.env.NEXT_PUBLIC_API_URL;
  if (!envUrl) return DEFAULT_API_BASE;

  // If running in browser on production domain (*iktravels.com) but env points to localhost, auto-fix to production API
  if (
    typeof window !== "undefined" &&
    window.location.hostname.includes("iktravels.com") &&
    (envUrl.includes("localhost") || envUrl.includes("127.0.0.1"))
  ) {
    return DEFAULT_API_BASE;
  }

  // If on server in production NODE_ENV, prevent localhost leaks
  if (
    typeof window === "undefined" &&
    process.env.NODE_ENV === "production" &&
    (envUrl.includes("localhost") || envUrl.includes("127.0.0.1"))
  ) {
    return DEFAULT_API_BASE;
  }

  return envUrl.replace(/\/+$/, "");
})();

/**
 * Resolves a full backend API URL for any endpoint path.
 * Prepares and prepends the backend base URL
 * and automatically formats `/api/` paths to include `/v1/` if missing.
 */
export function getApiUrl(path: string): string {
  if (!path) return API_BASE_URL;

  // If path is already a full URL, return as is
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  let cleanPath = path.startsWith("/") ? path : `/${path}`;

  // Automatically insert /v1 if missing from /api/ endpoints (e.g. /api/web/... -> /api/v1/web/...)
  if (cleanPath.startsWith("/api/") && !cleanPath.startsWith("/api/v1/")) {
    cleanPath = cleanPath.replace(/^\/api\//, "/api/v1/");
  }

  return `${API_BASE_URL}${cleanPath}`;
}

/**
 * Resolves a full storage URL for uploaded files / documents from the backend storage path.
 * Converts relative paths like `/storage/user_documents/xyz.pdf` into full backend URLs
 */
export function formatStorageUrl(path?: string | null): string {
  if (!path) return "";

  // Return blob preview URLs or data URIs as is
  if (path.startsWith("blob:") || path.startsWith("data:")) {
    return path;
  }

  let cleanPath = path;

  // Extract relative /storage/ path if URL contains localhost or 127.0.0.1
  if (cleanPath.includes("localhost") || cleanPath.includes("127.0.0.1")) {
    const storageIndex = cleanPath.indexOf("/storage/");
    if (storageIndex !== -1) {
      cleanPath = cleanPath.substring(storageIndex);
    }
  }

  // Ensure relative storage paths start with /storage/
  if (!cleanPath.startsWith("http://") && !cleanPath.startsWith("https://")) {
    if (!cleanPath.startsWith("/")) {
      cleanPath = `/${cleanPath}`;
    }
    if (!cleanPath.startsWith("/storage/")) {
      cleanPath = `/storage${cleanPath}`;
    }
    cleanPath = `${API_BASE_URL}${cleanPath}`;
  }

  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    cleanPath = cleanPath.replace(/^http:/, "https:");
  }

  return cleanPath;
}
